import React, { useCallback, useEffect, useState } from 'react';
import { Form, Button, Row, Container } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { getDataFromServer, postDatatoServer } from '../../utils/apiBase';
import ContextHelper from '../../ContextHooks/ContextHelper';
import { Delete, Edit } from '@mui/icons-material';
import CommonTable from '../table/CommonTable';
import DeleteModal from '../DeleteModal';
import { FormField } from '../formFields/FormField';

const StudyAdd = () => {
  const { currentUser } = ContextHelper();

  const [formData, setFormData] = useState({
    contrastType: '',
    modality: '',
    study: '',
    tenantPrivatePrice: '',
    tenantGovernmentPrice: '',
  });

  const [errors, setErrors] = useState({});
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [studyToggle, setStudyToggle] = useState(false);
  const [studyData, setStudyData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState(null);

  useEffect(() => {
    if (studyToggle) {
      setIsEditing(false);
      fetchStudyData();
    }
  }, [studyToggle]);

  useEffect(() => {
    if (!isEditing) {
      resetForm();
    }
  }, [isEditing]);

  const fetchStudyData = async () => {
    const ApiUrl = 'get-hospital-configs';

    const params = {
      end_point: ApiUrl,
      params: { token: currentUser?.token },
      call_back: handleResponse,
      props: '',
    };

    try {
      await getDataFromServer(params);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Failed to fetch patient data.');
      setLoading(false);
    }
  };

  const handleResponse = (res) => {
    setLoading(false);
    if (res.status) {
      const responseData = Array.isArray(res?.response) ? res.response : [res.response];
      setStudyData(responseData);
    } else {
      toast.error(res.message || 'Failed to fetch patients.');
    }
  };

  const handleEdit = useCallback((row) => {
    setIsEditing(true);
    setStudyToggle(false);
    setSelectedStudy(row);
    setFormData({
      contrastType: (row.contrast && 'Contrast') || 'Plain',
      modality: row.Modality,
      study: row.studyName,
      tenantGovernmentPrice: row.price,
      tenantPrivatePrice: row.pvtPrice,
    });
  }, []);

  const handleDelete = useCallback((row) => {
    setSelectedStudy(row);
    setDeleteModal(true);
  }, []);

  const closeModal = () => {
    setDeleteModal(false);
    setSelectedStudy(null);
  };

  const handleDeleteReport = () => {
    const requestBody = {
      ...selectedStudy,
      delete: true,
    };

    postDatatoServer({
      end_point: 'addOrUpdateStudies',
      body: requestBody,
      call_back: (res) => {
        if (res.response.status) {
          toast.success(res.response.message);
          closeModal();
          fetchStudyData();
        } else {
          toast.error(res.response.message);
        }
      },
      props: { header: true, token: currentUser?.token },
    });
  };

  const renderContrast = useCallback((value, row) => <span> {(value && 'Contrast') || 'Plain'}</span>, []);

  const renderAction = useCallback(
    (value, row) => {
      return (
        <>
          <Button variant="outline-success" className="py-1 px-2 me-2" onClick={() => handleEdit(row)}>
            <Edit sx={{ fontSize: '20px' }} />
          </Button>
          <Button variant="outline-danger" className="py-1 px-2" onClick={() => handleDelete(row)}>
            <Delete sx={{ fontSize: '20px' }} />
          </Button>
        </>
      );
    },
    [handleEdit, handleDelete]
  );

  const columns = [
    { accessor: 'contrast', label: 'Contrast Type', render: renderContrast },
    { accessor: 'Modality', label: 'Modality' },
    { accessor: 'studyName', label: 'Study' },
    { accessor: 'price', label: 'Government Price' },
    { accessor: 'pvtPrice', label: 'Private Price' },
    { accessor: 'action', label: 'Actions', render: renderAction },
  ];
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['contrastType', 'modality', 'study', 'tenantPrivatePrice', 'tenantGovernmentPrice'];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        const formattedField =
          field
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .charAt(0)
            .toUpperCase() + field.replace(/([a-z])([A-Z])/g, '$1 $2').slice(1);
        newErrors[field] = `${formattedField} is required`;
      }
    });

    if (formData.tenantPrivatePrice && isNaN(Number(formData.tenantPrivatePrice))) {
      newErrors.tenantPrivatePrice = 'Price must be a valid number';
    }
    if (formData.tenantGovernmentPrice && isNaN(Number(formData.tenantGovernmentPrice))) {
      newErrors.tenantGovernmentPrice = 'Price must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (validateForm()) {
      setLoading(true);
      const payload = {
        id: selectedStudy ? selectedStudy.id : undefined,
        studyName: formData.study,
        contrast: formData.contrastType.toLowerCase() === 'contrast',
        price: formData.tenantGovernmentPrice,
        pvtPrice: formData.tenantGovernmentPrice,
        Modality: formData.modality,
      };

      try {
        const res = await postDatatoServer({
          end_point: 'addOrUpdateStudies',
          body: payload,
          call_back: (res) => {
            if (res.response.status) {
              toast.success(res.response.message);
              resetForm();
              setIsEditing(false);
              setStudyToggle(true);
              fetchStudyData();
            } else {
              toast.error(res.response.message);
            }
          },
          props: { header: true, token: currentUser?.token },
        });
      } catch (error) {
        console.error('Error adding/updating hospital configurations:', error);
        toast.error('An error occurred, please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      contrastType: '',
      modality: '',
      study: '',
      tenantPrivatePrice: '',
      tenantGovernmentPrice: '',
    });
    setErrors({});
    setValidated(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const isValidAmount = /^(\d+(\.\d{0,2})?)?$/.test(value);

    if ((name === 'tenantPrivatePrice' || name === 'tenantGovernmentPrice') && !isValidAmount && value !== '') {
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
    }
  };

  return (
    <>
      <DeleteModal
        deleteModal={deleteModal}
        handleDeleteReport={handleDeleteReport}
        closeModal={closeModal}
        loading={loading}
        text={'Study'}
      />
      <Container fluid className="px-0">
        <div className="bg-white p-4 min-vh-100 shadow-sm">
          <div className="d-flex justify-content-between">
            <h3 className="base-color mb-2">
              {studyToggle ? 'Study Details' : isEditing ? 'Update Study' : 'Add Study'}
            </h3>
            <div>
              <Button
                variant=""
                className="login-btn fs-15 base-color-bg text-white fw-semibold"
                onClick={() => setStudyToggle(!studyToggle)}
              >
                {studyToggle ? 'Add Study' : 'Study Details'}
              </Button>
            </div>
          </div>
          {(studyToggle && studyData.length > 0 && (
            <CommonTable data={studyData} columns={columns} loading={loading} />
          )) || (
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              <Row>
                <FormField
                  label="Contrast Type"
                  name="contrastType"
                  value={formData.contrastType}
                  onChange={handleChange}
                  error={errors.contrastType}
                  required
                  options={['Contrast', 'Plain']}
                />

                <FormField
                  label="Tenant Private Price"
                  name="tenantPrivatePrice"
                  value={formData.tenantPrivatePrice}
                  onChange={handleChange}
                  error={errors.tenantPrivatePrice}
                  required
                />

                <FormField
                  label="Tenant Government Price"
                  name="tenantGovernmentPrice"
                  value={formData.tenantGovernmentPrice}
                  onChange={handleChange}
                  error={errors.tenantGovernmentPrice}
                  required
                />

                <FormField
                  label="Modality"
                  name="modality"
                  value={formData?.modality
                    ?.replace(/^\s+/, '')
                    .replace(/\s{2,}/g, ' ')
                    .trimStart()}
                  onChange={handleChange}
                  error={errors.modality}
                  required
                />
                <FormField
                  label="Study Name"
                  name="study"
                  value={formData?.study
                    ?.replace(/^\s+/, '')
                    .replace(/\s{2,}/g, ' ')
                    .trimStart()}
                  onChange={handleChange}
                  error={errors.study}
                  required
                />
              </Row>

              <Button type="submit" className="login-btn base-color-bg text-whit px-4" disabled={loading}>
                {loading ? 'Saving...' : isEditing ? 'Update' : 'Save'}
              </Button>
            </Form>
          )}
        </div>
      </Container>
    </>
  );
};

export default StudyAdd;
